import {
    AREA_CODES,
    ECountryCode,
} from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import { getCountryCodeFromPhone } from './getCountryCodeFromPhone';
import { normalizePhoneNumber } from './normalizePhoneNumber';
import { removeCountryCodeFromPhone } from './removeCountryCodeFromPhone';
import { hasInternationalPhonePrefix } from './hasInternationalPhonePrefix';

const applyPhoneFormat = (format: string, localDigits: string): string => {
    let digitIndex = 0;

    return format.replace(/#/g, () => localDigits[digitIndex++] ?? '');
};

export type TFormatDisplayPhoneOptions = {
    /** Country inferred from normalizedPhone (used when the number has no intl prefix). */
    countryCode?: ECountryCode;
    /** Last resort when the country cannot be determined from the number. */
    fallbackCountryCode?: ECountryCode;
};

/**
 * Formats a phone for display, e.g. (+52) 55 1234 5678.
 * Prefers LADA detected from normalizedPhone (+521/+52, +1, etc.).
 */
export const formatDisplayPhone = (
    phone?: string,
    options: TFormatDisplayPhoneOptions | ECountryCode = ECountryCode.Mexico
): string => {
    const resolvedOptions: TFormatDisplayPhoneOptions =
        typeof options === 'string'
            ? { fallbackCountryCode: options }
            : options;

    const fallbackCountryCode =
        resolvedOptions.fallbackCountryCode ?? ECountryCode.Mexico;

    if (!phone?.trim()) return '';

    const normalized = normalizePhoneNumber(phone);
    const digitsOnly = normalized.replace(/\D/g, '');

    if (!digitsOnly) return phone.trim();

    const detectedCountryCode = getCountryCodeFromPhone(
        digitsOnly,
        fallbackCountryCode
    );

    const countryCode =
        resolvedOptions.countryCode && !hasInternationalPhonePrefix(digitsOnly)
            ? resolvedOptions.countryCode
            : detectedCountryCode;

    const areaConfig =
        AREA_CODES[countryCode] || AREA_CODES[fallbackCountryCode];

    const phoneWithPlus = normalized.startsWith('+')
        ? normalized
        : `+${digitsOnly}`;

    let localDigits = removeCountryCodeFromPhone(phoneWithPlus).replace(
        /\D/g,
        ''
    );

    if (
        countryCode === ECountryCode.Mexico &&
        localDigits.startsWith('1') &&
        localDigits.length === 11
    ) {
        localDigits = localDigits.slice(1);
    }

    if (!localDigits) return phone.trim();

    return applyPhoneFormat(areaConfig.format, localDigits);
};
